import { applicantSection, feeInfo, otherAttachments, replySection } from "modules/ebiForms/common";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * C4. Zgłoszenie przemocy domowej (procedura „Niebieskie Karty”) — art. 9d ust. 1 i 4 ustawy z 29.07.2005 r.
 * o przeciwdziałaniu przemocy domowej; rozporządzenie RM z 6.09.2023 r. w sprawie procedury „Niebieskie Karty” oraz wzorów
 * formularzy (Dz.U. poz. 1870). To NIE jest wniosek o „Niebieską Kartę”: procedurę wszczynają służby (formularz „Niebieska
 * Karta — A”), także po zgłoszeniu świadka, bez zgody osoby doznającej i osoby stosującej przemoc. Interesant składa
 * zgłoszenie — bez wzoru i bez opłaty, z dobrowolnymi dowodami (katalog, „Uwagi dla EBI”). Zgłoszenie w EBI nie jest
 * anonimowe — zgłaszający jest znany z mObywatela. Formularze NK A–D wypełniają służby, więc nie odwzorowano ich pól;
 * pytania ograniczone do tego, co pozwala zespołowi interdyscyplinarnemu podjąć działania.
 */
const WITNESS: EbiCondition = { field: "zglaszajacy", equals: "swiadek" };

const C4: IEbiFormDefinition = {
  code: "C4",
  version: 1,
  title: "Zgłoszenie przemocy domowej",
  category: EEbiFormCategory.Social,
  office: EEbiFormOffice.MunicipalUnit,
  documentType: "Pismo",
  description:
    "Zgłoś przemoc domową — doznawaną przez Ciebie albo taką, której jesteś świadkiem. Zgłoszenie w EBI nie jest anonimowe: urząd otrzyma Twoje dane z mObywatela. W nagłym zagrożeniu dzwoń pod 112.",
  keywords: ["przemoc domowa", "niebieska karta", "przemoc w rodzinie", "zespół interdyscyplinarny", "zgłoszenie", "ops"],
  organ: "Przewodniczący zespołu interdyscyplinarnego — za pośrednictwem ośrodka pomocy społecznej (centrum usług społecznych)",
  legalBasis:
    "art. 9d ust. 1 i 4 ustawy z dnia 29 lipca 2005 r. o przeciwdziałaniu przemocy domowej; rozporządzenie Rady Ministrów z dnia 6 września 2023 r. w sprawie procedury „Niebieskie Karty” oraz wzorów formularzy „Niebieska Karta”",
  request: "Zgłaszam podejrzenie stosowania przemocy domowej opisane poniżej i proszę o podjęcie działań w ramach procedury „Niebieskie Karty”.",
  feeLabel: "bez opłaty",
  fee: "Bez opłaty.",
  sections: [
    {
      code: "informacja",
      title: "Zanim zgłosisz",
      fields: [
        feeInfo(
          "infoTelefony",
          "Jeśli komuś grozi niebezpieczeństwo teraz — zadzwoń pod numer alarmowy 112. Wsparcie i porady dla osób doznających przemocy domowej: bezpłatny telefon 800 120 002.",
        ),
        feeInfo(
          "infoProcedura",
          "Procedura „Niebieskie Karty” toczy się bez zgody osoby doznającej przemocy i osoby stosującej przemoc (art. 9d ust. 1 ustawy). Zgłoszenie złożone w Elektronicznym Biurze Interesanta nie jest anonimowe — urząd otrzyma Twoje imię, nazwisko i PESEL z mObywatela. Zgłoszenie anonimowe możesz złożyć inną drogą, np. telefonicznie lub listownie w ośrodku pomocy społecznej.",
        ),
      ],
    },
    applicantSection("Zgłaszający"),
    {
      code: "zgloszenie",
      title: "Zgłoszenie",
      fields: [
        {
          code: "zglaszajacy",
          type: "radio",
          label: "Zgłaszam jako",
          required: true,
          options: [
            { value: "osobaDoznajaca", label: "osoba doznająca przemocy domowej" },
            { value: "swiadek", label: "świadek — przemocy doznaje inna osoba" },
          ],
        },
        {
          code: "osobaDoznajaca",
          type: "text",
          label: "Imię i nazwisko osoby doznającej przemocy",
          visibleWhen: WITNESS,
          maxLength: 200,
          hint: "Jeśli je znasz.",
        },
        {
          code: "adresZdarzen",
          type: "text",
          label: "Adres, pod którym dochodzi do przemocy",
          required: true,
          visibleWhen: WITNESS,
          maxLength: 300,
          hint: "Miejscowość, ulica, numer domu i lokalu.",
        },
        {
          code: "adresInny",
          type: "text",
          label: "Adres, pod którym dochodzi do przemocy (jeśli inny niż Twój adres zamieszkania)",
          visibleWhen: { field: "zglaszajacy", equals: "osobaDoznajaca" },
          maxLength: 300,
        },
        {
          code: "osobaStosujaca",
          type: "text",
          label: "Osoba stosująca przemoc — imię i nazwisko albo relacja (np. mąż, ojciec, syn)",
          maxLength: 200,
          hint: "Nieobowiązkowo.",
        },
        {
          code: "dzieci",
          type: "radio",
          label: "Czy przemoc dotyczy dzieci albo dzieje się w ich obecności?",
          options: [
            { value: "tak", label: "Tak" },
            { value: "nie", label: "Nie" },
            { value: "nieWiem", label: "Nie wiem" },
          ],
        },
        {
          code: "opis",
          type: "textarea",
          label: "Opis sytuacji",
          required: true,
          maxLength: 10000,
          hint: "Co się dzieje, od kiedy i jak często. Podaj tyle, ile chcesz — szczegóły ustali z Tobą pracownik.",
        },
        {
          code: "bezpiecznyKontakt",
          type: "text",
          label: "Bezpieczny sposób i pora kontaktu",
          maxLength: 300,
          hint: "Nieobowiązkowo — np. tylko przez EBI albo telefon po 16:00, jeśli list lub telefon mógłby Ci zaszkodzić.",
        },
      ],
    },
    replySection("Sposób kontaktu w sprawie zgłoszenia"),
  ],
  attachments: [
    {
      code: "dowody",
      label: "Dokumenty potwierdzające przemoc",
      hint: "Nieobowiązkowo — np. zaświadczenie lekarskie o przyczynach i rodzaju uszkodzeń ciała, dokumentacja medyczna, notatki Policji, nagrania, orzeczenia sądu.",
      multiple: true,
    },
    otherAttachments(),
  ],
};

export default C4;
